import { v4 as uuidv4 } from "uuid";
import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { tokenManager, handleApiError } from "@inductiveautomation/ws-components";

export const AUTH_SKIP_HEADER = "X-Skip-Auth";

const getBaseUrl = () => {
  if (process.env.NODE_ENV === "test") return "http://localhost:1234";

  return window._acctConfig?.API_SERVER_URL ?? "";
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl: getBaseUrl(),
  prepareHeaders: async (headers: Headers) => {
    if(headers.has(AUTH_SKIP_HEADER)) {
      headers.delete(AUTH_SKIP_HEADER);
      // Don't set Authorization header
    } else {
      const token = await tokenManager.refreshToken();
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
    }
    headers.set("X-IA-Request-Id", uuidv4());

    return headers;
  },
});

const dynamicBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // NOTE: (2024-11-21) Noah Settersten => Strip out the invalid /api prefix from the generated endpoints. The spec for
  // the server API includes this right now (as it's present internally to the API), but after a proxy this prefix no
  // longer exists in the URL.
  // For reference, see the docs at https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#constructing-a-dynamic-base-url-using-redux-state.
  const endpointUrl = typeof args === "string" ? args : args.url;
  const adjustedUrl = endpointUrl.replace("/api", "");
  const adjustedArgs =
    typeof args === "string" ? adjustedUrl : { ...args, url: adjustedUrl };

  const result = await rawBaseQuery(adjustedArgs, api, extraOptions);

  // If the request resulted in an error, dispatch an event with the error details to be handled by useApiErrorHandler().
  if (result.error) {
    handleApiError({ result, endpointUrl });
  }

  return result;
};

export const baseApi = createApi({
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "Profile",
    "EmailPreferences",
    "MakerLicenses",
    "Certifications",
    "Organizations",
  ],
  endpoints: (builder) => ({
    putAvatarImage: builder.mutation({
      query: (formData: FormData) => ({
        url: "/account/profile/avatarImage",
        method: "PUT",
        body: formData,
      }),
    }),
    // from the docs: "If query doesn't have a parameter, then void type has to be provided explicitly"
    // https://redux-toolkit.js.org/rtk-query/usage-with-typescript#typing-query-and-mutation-endpoints
    // using undefined forces the function to pass undefined as a parameter
    // using void directly triggers this lint error: eslint/no-invalid-void-type
    // rule already marked off in the eslint config for the generated-api
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    deleteAvatarImage: builder.mutation<string, void>({
      query: () => ({
        url: "/account/profile/avatarImage",
        method: "DELETE",
      }),
    }),
  }),
});
